import { LeftOutlined, RightOutlined, SwapOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Drawer, Space, Tabs, Tag, Tooltip, Typography } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  AssignmentCandidateRow,
  ListResponse,
  SubstitutionDateRow,
  SubstitutionRequestRow,
  SubstitutionResponse,
  SubstitutionSwapRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";
import { CandidateInfoPanel } from "../components/CandidateInfoPanel";
import { SpeakerListPreferences } from "../components/SpeakerListPreferences";
import { SpeakerName, speakerNameText } from "../components/SpeakerName";

/**
 * Sidebar entry "local_speaker_substitustion" →
 * /local_assignation/local_speaker_substitution.
 *
 * The coordinator's half of a flow that starts on the speaker's screen: a
 * speaker who cannot keep a date asks to be moved and marks which weeks would
 * suit them. This screen is where that request is resolved, and there are three
 * ways out — each its own tab, because they lead to different outcomes:
 *
 *  - a different week for the same speaker and talk
 *  - a swap with another week's assignment
 *  - a different speaker altogether
 *
 * All three go through the same rule checks as a fresh assignment, so the
 * server may still decline; it says why in a notification.
 */
export function SubstitutionPage() {
  const { t } = useTranslation();

  const [resolving, setResolving] = useState<SubstitutionRequestRow | null>(null);

  const requests = useQuery({
    queryKey: ["local_assignation", "substitutions"],
    queryFn: () =>
      apiFetch<ListResponse<SubstitutionRequestRow>>("/local_assignation/substitutions"),
  });

  const columns = useMemo<ExportableColumn<SubstitutionRequestRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 200,
        // The two marks the old grid drew beside the date: a red one for the
        // weeks whose speaker asked to be moved, a green one for what changed
        // since the last login. Under the date, so it keeps its line.
        render: (value: string, row) => (
          <Space orientation="vertical" size={0} align="start">
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            {(row.required_substitution || row.modified) && (
              <Space size={4} wrap>
                {row.required_substitution && (
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>
                    {t("local_assignation.required_substitution")}
                  </Tag>
                )}
                {row.modified && (
                  <Tag color="green" style={{ marginInlineEnd: 0 }}>
                    {t("icon.modified")}
                  </Tag>
                )}
              </Space>
            )}
          </Space>
        ),
        exportValue: (row) =>
          [
            row.week_label,
            row.required_substitution ? t("local_assignation.required_substitution") : null,
            row.modified ? t("icon.modified") : null,
          ]
            .filter(Boolean)
            .join(" · "),
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_id"),
        dataIndex: "speaker_name",
        width: 190,
        ellipsis: true,
        render: (_value: string | null, row) => (
          <SpeakerName
            name={row.speaker_name}
            contactMe={row.speaker_contact_me}
            hint={t("speaker.contact_me")}
          />
        ),
        exportValue: (row) => speakerNameText(row.speaker_name, row.speaker_contact_me, t),
      },
      {
        title: t("activerecord.models.circumscription"),
        dataIndex: "speaker_circumscription",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
        // A talk the speaker has since dropped still has to be recognisable —
        // replacing the week is exactly what one would do about it.
        render: (value: string | null, row) =>
          row.speech_inactive ? (
            <Tooltip title={t("speech.inactive")}>
              <Tag color="orange">{value}</Tag>
            </Tooltip>
          ) : (
            value
          ),
        exportValue: (row) =>
          row.speech_inactive
            ? `${row.speech_schema ?? ""} (${t("speech.inactive")})`
            : row.speech_schema,
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speaker_speech.additional_notes"),
        dataIndex: "speech_observations",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "speaker_phone",
        width: 140,
      },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "speaker_email",
        width: 200,
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_value, row) => (
          <Tooltip title={t("local_assignation.substitute")}>
            <Button size="small" icon={<SwapOutlined />} onClick={() => setResolving(row)} />
          </Tooltip>
        ),
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.substitutions")}>
      <DataTable<SubstitutionRequestRow>
        columns={columns}
        rows={requests.data?.data}
        exportAs={t("navigation.substitutions")}
        loading={requests.isPending}
        error={requests.error}
        rowKey="week_begin"
      />

      {resolving && (
        <ResolveDrawer request={resolving} onClose={() => setResolving(null)} />
      )}
    </Card>
  );
}

/** One row of the "same speaker" list — see the useMemo that builds it. */
interface SameSpeakerRow {
  key: string;
  kind: "swap" | "date";
  week_begin: string;
  week_label: string;
  week_day_label: string | null;
  day_time: string | null;
  preferred: boolean;
  speaker_speech_id: number | null;
  speaker_name: string | null;
  speaker_contact_me?: boolean;
  speaker_congregation: string | null;
  speech_schema: string | null;
  speech_title: string | null;
  /** False when that week's speaker no longer offers the talk they are down for. */
  speech_valid: boolean;
  /** The week to trade with; null on a row that only moves this assignment. */
  local_assignation_id: number | null;
}

function ResolveDrawer({
  request,
  onClose,
}: {
  request: SubstitutionRequestRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const base = `/local_assignation/events/${request.week_begin}/substitution`;

  // One panel for all three tabs, as each of them is another way of answering
  // the same question: it describes whatever row was clicked last. A row names
  // both halves — which pair, and in which week — because they differ per tab:
  // the alternative dates move the week, the swaps and the replacements change
  // the pair.
  const [info, setInfo] = useState<{ week: string; pair: number } | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const show = useCallback((week: string, pair: number | null) => {
    if (pair === null) return;
    setInfo({ week, pair });
    setPanelOpen(true);
  }, []);

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  /** All three resolutions answer the same way, so they share one mutation. */
  const resolve = useMutation({
    mutationFn: ({ path, body }: { path: string; body: Record<string, unknown> }) =>
      apiFetch<SubstitutionResponse>(`${base}/${path}`, { method: "PUT", body }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
      void queryClient.invalidateQueries({ queryKey: ["archive"] });

      // Once the exchange has taken place this week is settled, and after a
      // substitution by date it does not even hold an assignment any more —
      // the three lists below would ask for one and be answered 404. A refusal
      // leaves everything as it was, so the drawer stays open and the message
      // explains why.
      if (data.success) onClose();
    },
    onError: showError,
  });

  const dates = useQuery({
    queryKey: ["local_assignation", "substitution", "dates", request.week_begin],
    queryFn: () => apiFetch<ListResponse<SubstitutionDateRow>>(`${base}/dates`),
  });

  const swaps = useQuery({
    queryKey: ["local_assignation", "substitution", "swaps", request.week_begin],
    queryFn: () => apiFetch<ListResponse<SubstitutionSwapRow>>(`${base}/swaps`),
  });

  const speakers = useQuery({
    queryKey: ["local_assignation", "substitution", "speakers", request.week_begin],
    queryFn: () => apiFetch<ListResponse<AssignmentCandidateRow>>(`${base}/speakers`),
  });

  const busy = resolve.isPending;

  /**
   * The first tab of the old screen was a single list holding two kinds of row:
   * a week that is already taken, whose assignment would trade places with this
   * one, and a week that is simply free for the same speaker and talk. Two
   * endpoints answer them in this API, so they are put back together here — the
   * "Art" column is what the old grid marked with two different icons.
   */
  const sameSpeaker = useMemo<SameSpeakerRow[]>(
    () => [
      ...(swaps.data?.data ?? []).map((row) => ({
        key: `swap-${row.local_assignation_id}`,
        kind: "swap" as const,
        week_begin: row.week_begin,
        week_label: row.week_label,
        week_day_label: row.week_day_label,
        day_time: row.day_time,
        preferred: row.preferred,
        speaker_speech_id: row.speaker_speech_id,
        speaker_name: row.speaker_name,
        speaker_contact_me: row.speaker_contact_me,
        speaker_congregation: row.speaker_congregation,
        speech_schema: row.speech_schema,
        speech_title: row.speech_title,
        speech_valid: row.speech_valid,
        local_assignation_id: row.local_assignation_id,
      })),
      ...(dates.data?.data ?? []).map((row) => ({
        key: `date-${row.week_begin}`,
        kind: "date" as const,
        week_begin: row.week_begin,
        week_label: row.week_label,
        week_day_label: row.week_day_label,
        day_time: row.day_time,
        preferred: row.preferred,
        speaker_speech_id: row.speaker_speech_id,
        // These rows keep the assignment as it is and only move it, so the
        // speaker and the talk are the ones of the week being resolved.
        speaker_name: request.speaker_name,
        speaker_contact_me: request.speaker_contact_me,
        speaker_congregation: request.speaker_congregation,
        speech_schema: request.speech_schema,
        speech_title: request.speech_title,
        speech_valid: true,
        local_assignation_id: null,
      })),
    ],
    [swaps.data, dates.data, request],
  );

  const sameSpeakerColumns = useMemo<ExportableColumn<SameSpeakerRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.speaker_assignation_alternative.alternative_type"),
        dataIndex: "kind",
        width: 190,
        render: (_value: string, row) =>
          row.kind === "swap" ? (
            <Tag color="green">{t("local_assignation.substitute")}</Tag>
          ) : (
            <Tag>{t("local_assignation.alternative_assignation")}</Tag>
          ),
        exportValue: (row) =>
          row.kind === "swap"
            ? t("local_assignation.substitute")
            : t("local_assignation.alternative_assignation"),
      },
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 200,
        // Under the date rather than beside it, so the date keeps one line.
        render: (value: string, row) => (
          <Space orientation="vertical" size={0} align="start">
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            {/* The speaker named this week themselves — the least friction. */}
            {row.preferred && (
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                {t("activerecord.attributes.speaker_assignation_alternative.preference")}
              </Tag>
            )}
          </Space>
        ),
        exportValue: (row) =>
          row.preferred
            ? `${row.week_label} (${t("activerecord.attributes.speaker_assignation_alternative.preference")})`
            : row.week_label,
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_id"),
        dataIndex: "speaker_name",
        width: 180,
        ellipsis: true,
        render: (_value: string | null, row) => (
          <SpeakerName
            name={row.speaker_name}
            contactMe={row.speaker_contact_me}
            hint={t("speaker.contact_me")}
          />
        ),
        exportValue: (row) => speakerNameText(row.speaker_name, row.speaker_contact_me, t),
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
        render: (value: string | null, row) =>
          row.speech_valid ? (
            value
          ) : (
            // The swap still works; that speaker just no longer offers this
            // talk, so it has to be picked again afterwards.
            <Tooltip title={t("local_assignation.original_speech_no_more_available")}>
              <Space size={4}>
                {value}
                <Tag color="orange">{t("local_assignation.change_speech")}</Tag>
              </Space>
            </Tooltip>
          ),
        exportValue: (row) =>
          row.speech_valid
            ? row.speech_title
            : `${row.speech_title ?? ""} (${t("local_assignation.change_speech")})`,
      },
      {
        key: "actions",
        width: 90,
        fixed: "right",
        render: (_value, row) => (
          <Button
            size="small"
            type={row.preferred ? "primary" : "default"}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              // The two kinds of row are resolved by two different endpoints:
              // one moves this assignment, the other trades it for another.
              if (row.kind === "swap") {
                resolve.mutate({
                  path: "swap",
                  body: { alternative_local_assignation_id: row.local_assignation_id },
                });
              } else {
                resolve.mutate({ path: "date", body: { alternative_date: row.week_begin } });
              }
            }}
          >
            {t("speaker_assignation_alternative.accept_substitution")}
          </Button>
        ),
      },
    ],
    [t, busy, resolve],
  );

  const speakerColumns = useMemo<TableProps<AssignmentCandidateRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 140,
        sorter: (a, b) => compareText(a.first_name, b.first_name),
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 140,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
        sorter: (a, b) => compareNumeric(a.speech_schema, b.speech_schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
      },
      {
        key: "actions",
        width: 80,
        fixed: "right",
        render: (_value, row) => (
          <Button
            size="small"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              resolve.mutate({ path: "speaker", body: { speaker_speech_id: row.id } });
            }}
          >
            {t("local_assignation.select_speaker")}
          </Button>
        ),
      },
    ],
    [t, busy, resolve],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={panelOpen ? "min(1600px, 96vw)" : "min(1100px, 96vw)"}
      destroyOnHidden
      title={
        <Typography.Text>
          {[request.week_label, request.speaker_name, request.speech_schema]
            .filter(Boolean)
            .join(" · ")}
        </Typography.Text>
      }
      extra={
        <Button
          icon={panelOpen ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => setPanelOpen((open) => !open)}
        >
          {t("local_assignation.search_speaker_info")}
        </Button>
      }
    >
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Both lists below are built from these two, so they sit above the
              tabs rather than in each of them — the old screen carried them in
              both toolbars, writing the same setting either way. */}
          <SpeakerListPreferences disabled={busy} />

          <Tabs
            items={[
              {
                key: "same_speaker",
                label: t("local_assignation.speaker_substitution"),
                children: (
                  <DataTable<SameSpeakerRow>
                    columns={sameSpeakerColumns}
                    rows={sameSpeaker}
                    exportAs={`${t("local_assignation.speaker_substitution")} · ${request.week_label}`}
                    loading={dates.isPending || swaps.isPending || busy}
                    error={dates.error ?? swaps.error}
                    rowKey="key"
                    // A week whose speaker dropped the talk has no pair left to
                    // describe; the list marks it either way.
                    onRowClick={(row) => show(row.week_begin, row.speaker_speech_id)}
                    rowClassName={(row) =>
                      row.week_begin === info?.week ? "substitution-selected" : ""
                    }
                  />
                ),
              },
              {
                key: "speakers",
                label: t("local_assignation.speaker_replace"),
                children: (
                  <DataTable<AssignmentCandidateRow>
                    columns={speakerColumns}
                    rows={speakers.data?.data}
                    exportAs={`${t("local_assignation.speaker_replace")} · ${request.week_label}`}
                    loading={speakers.isPending || busy}
                    error={speakers.error}
                    // The week stays; it is the pair that would change.
                    onRowClick={(row) => show(request.week_begin, row.id)}
                    rowClassName={(row) => (row.id === info?.pair ? "substitution-selected" : "")}
                  />
                ),
              },
            ]}
          />
        </div>

        {panelOpen && (
          <div
            style={{
              width: 420,
              flexShrink: 0,
              borderInlineStart: "1px solid #f0f0f0",
              paddingInlineStart: 16,
              maxHeight: "calc(100vh - 200px)",
              overflow: "auto",
            }}
          >
            <CandidateInfoPanel
              weekBegin={info?.week ?? null}
              speakerSpeechId={info?.pair ?? null}
              placeholder={t("local_assignation.select_speaker")}
            />
          </div>
        )}
      </div>

      <style>{`.substitution-selected > td { background: rgba(22, 119, 255, 0.06); }`}</style>
    </Drawer>
  );
}
